import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { collection, getDocs, query, orderBy, onSnapshot, doc, getDoc, updateDoc, addDoc, deleteDoc, writeBatch } from 'firebase/firestore'
import { useEffect, useMemo } from 'react'
import { db } from '@/config/firebase'
import { Invoice, Vehicle, BankProfile } from '@otw/models'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { pandaApi } from '@/core/api/modules/pandaApi'

export const useInvoices = () => {
  const queryClient = useQueryClient()
  const { showSuccess, showError } = useSnackbar()

  const queryKey = ['invoices']
  const invoicesRef = collection(db, 'invoices')
  const invoicesQuery = query(invoicesRef, orderBy('time_created', 'desc'))

  const resolveBankProfileEmails = (bankProfile: BankProfile, fallbackEmail?: string): string[] => {
    const profileType = bankProfile.profileType || 'sales'

    if (profileType === 'inventory') {
      const inventoryEmail = bankProfile.email?.trim()
      if (inventoryEmail) {
        return [inventoryEmail]
      }
      return fallbackEmail ? [fallbackEmail] : []
    }

    const salesEmails = (bankProfile.bankContacts || [])
      .map(contact => contact.email?.trim())
      .filter((email): email is string => Boolean(email))

    if (salesEmails.length > 0) {
      return salesEmails
    }

    return fallbackEmail ? [fallbackEmail] : []
  }

  const {
    data: invoices,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async (): Promise<Invoice[]> => {
      const snapshot = await getDocs(invoicesQuery)
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Invoice[]
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  // Set up real-time snapshot listener
  useEffect(() => {
    const unsubscribe = onSnapshot(
      invoicesQuery,
      snapshot => {
        const updatedInvoices = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as Invoice[]

        // Update the cache with new data
        queryClient.setQueryData(queryKey, updatedInvoices)
      },
      (error) => {
        console.error('Error in invoices snapshot listener:', error)
      }
    )

    return () => unsubscribe()
  }, [queryClient, queryKey])

  const sendInvoice = useMutation({
    mutationFn: async (invoiceId: string) => {
      const invoiceRef = doc(db, 'invoices', invoiceId)
      const invoiceDoc = await getDoc(invoiceRef)
      if (!invoiceDoc.exists()) {
        throw new Error('Invoice not found')
      }

      const invoiceData = invoiceDoc.data() as Invoice

      // Keep recipient fields aligned with the selected bank profile before sending
      // so Purchase Orders / Payoff Authorizations route emails correctly.
      if (invoiceData.bank_profile_id) {
        const bankProfileDoc = await getDoc(doc(db, 'bank_profiles', invoiceData.bank_profile_id))
        if (bankProfileDoc.exists()) {
          const bankProfile = { id: bankProfileDoc.id, ...bankProfileDoc.data() } as BankProfile
          const fallbackEmail = invoiceData.recipient_email || invoiceData.client_email
          const recipientEmails = resolveBankProfileEmails(bankProfile, fallbackEmail)

          if (recipientEmails.length === 0) {
            throw new Error('Selected bank profile has no valid recipient email')
          }

          const primaryRecipient = recipientEmails[0]
          const updates: Partial<Invoice> = {}

          if (invoiceData.recipient_email !== primaryRecipient) {
            updates.recipient_email = primaryRecipient
          }
          if (invoiceData.client_email !== primaryRecipient) {
            updates.client_email = primaryRecipient
          }
          if (Object.keys(updates).length > 0) {
            await updateDoc(invoiceRef, updates)
          }
        }
      }

      return await pandaApi.admin.sendInvoice(invoiceId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      showSuccess('Invoice sent successfully')
    },
    onError: (error) => {
      showError(`Failed to send invoice: ${error instanceof Error ? error.message : 'Unknown error'}`)
    },
  })

  // Fetch vehicles for all invoices
  const vehicleIds = useMemo(() => {
    const allVehicleIds = new Set<string>()
    const vehicleIdToInvoiceMap = new Map<string, { invoiceId: string; inventoryType: 'master_inventory' | 'shield_inventory' }>()
    
    if (invoices) {
      invoices.forEach(invoice => {
        invoice.vehicle_ids?.forEach(vehicleId => {
          allVehicleIds.add(vehicleId)
          vehicleIdToInvoiceMap.set(vehicleId, {
            invoiceId: invoice.id,
            inventoryType: invoice.inventory_type,
          })
        })
      })
    }
    
    return { allVehicleIds: Array.from(allVehicleIds), vehicleIdToInvoiceMap }
  }, [invoices])

  const {
    data: vehiclesMap = new Map<string, Vehicle>(),
    isLoading: isLoadingVehicles,
  } = useQuery({
    queryKey: ['invoice-vehicles', vehicleIds.allVehicleIds.sort().join(',')],
    queryFn: async (): Promise<Map<string, Vehicle>> => {
      const vehicles = new Map<string, Vehicle>()
      
      // Batch fetch all vehicles
      const vehiclePromises = vehicleIds.allVehicleIds.map(async (vehicleId) => {
        // Try to find which invoice has this vehicle to determine inventory type
        const invoiceInfo = vehicleIds.vehicleIdToInvoiceMap.get(vehicleId)
        const inventoryType = invoiceInfo?.inventoryType || 'master_inventory'
        
        try {
          const vehicleDoc = await getDoc(doc(db, inventoryType, vehicleId))
          if (vehicleDoc.exists()) {
            return { id: vehicleDoc.id, ...vehicleDoc.data() } as Vehicle
          }
          // If not found in primary inventory, try the other one
          const otherInventoryType = inventoryType === 'master_inventory' ? 'shield_inventory' : 'master_inventory'
          const otherVehicleDoc = await getDoc(doc(db, otherInventoryType, vehicleId))
          if (otherVehicleDoc.exists()) {
            return { id: otherVehicleDoc.id, ...otherVehicleDoc.data() } as Vehicle
          }
        } catch (error) {
          console.error(`Error fetching vehicle ${vehicleId}:`, error)
        }
        return null
      })
      
      const vehicleResults = await Promise.all(vehiclePromises)
      vehicleResults.forEach(vehicle => {
        if (vehicle) {
          vehicles.set(vehicle.id, vehicle)
        }
      })
      
      return vehicles
    },
    enabled: vehicleIds.allVehicleIds.length > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  // Fetch bank profiles for Retail Orders
  const bankProfileIds = useMemo(() => {
    const allBankProfileIds = new Set<string>()
    
    if (invoices) {
      invoices.forEach(invoice => {
        if (invoice.template_name?.startsWith('Retail Order') && invoice.bank_profile_id) {
          allBankProfileIds.add(invoice.bank_profile_id)
        }
      })
    }
    
    return Array.from(allBankProfileIds)
  }, [invoices])

  const {
    data: bankProfilesMap = new Map<string, BankProfile>(),
    isLoading: isLoadingBankProfiles,
  } = useQuery({
    queryKey: ['invoice-bank-profiles', bankProfileIds.sort().join(',')],
    queryFn: async (): Promise<Map<string, BankProfile>> => {
      const bankProfiles = new Map<string, BankProfile>()
      
      // Batch fetch all bank profiles
      const bankProfilePromises = bankProfileIds.map(async (bankProfileId) => {
        try {
          const bankProfileDoc = await getDoc(doc(db, 'bank_profiles', bankProfileId))
          if (bankProfileDoc.exists()) {
            return { id: bankProfileDoc.id, ...bankProfileDoc.data() } as BankProfile
          }
        } catch (error) {
          console.error(`Error fetching bank profile ${bankProfileId}:`, error)
        }
        return null
      })
      
      const bankProfileResults = await Promise.all(bankProfilePromises)
      bankProfileResults.forEach(bankProfile => {
        if (bankProfile) {
          bankProfiles.set(bankProfile.id, bankProfile)
        }
      })
      
      return bankProfiles
    },
    enabled: bankProfileIds.length > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  // Generate invoice number for PDF invoices
  const generateInvoiceNumber = async (): Promise<string> => {
    const year = new Date().getFullYear();
    const prefix = `INV${year}`;
    
    // Get all invoices to find the highest number
    const invoicesSnapshot = await getDocs(invoicesRef);
    
    let maxNumber = 0;
    invoicesSnapshot.docs.forEach(doc => {
      const invoiceNumber = doc.data()?.invoice_number;
      if (invoiceNumber && invoiceNumber.startsWith(prefix)) {
        // Extract number part (everything after the year)
        const numberPart = invoiceNumber.substring(prefix.length);
        const num = parseInt(numberPart, 10);
        if (!isNaN(num) && num > maxNumber) {
          maxNumber = num;
        }
      }
    });
    
    const nextNumber = maxNumber + 1;
    return `${prefix}${String(nextNumber).padStart(4, '0')}`;
  };

  /**
   * Convert a file URL to base64 string for email attachment
   */
  const urlToBase64 = async (url: string): Promise<string> => {
    try {
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`)
      }
      
      const blob = await response.blob()
      
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          const base64String = reader.result as string
          // Remove the data:...;base64, prefix if it exists
          const base64Data = base64String.split(',')[1] || base64String
          resolve(base64Data)
        }
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
    } catch (error) {
      console.error('Error converting URL to base64:', error)
      throw error
    }
  };

  const sendPdfInvoiceToBank = useMutation({
    mutationFn: async (invoiceId: string) => {
      // Get the invoice
      const invoiceRef = doc(db, 'invoices', invoiceId)
      const invoiceDoc = await getDoc(invoiceRef)
      
      if (!invoiceDoc.exists()) {
        throw new Error('Invoice not found')
      }
      
      const invoiceData = invoiceDoc.data() as Invoice
      
      if (!invoiceData.pdf_url) {
        throw new Error('Invoice does not have a PDF URL')
      }
      
      if (!invoiceData.bank_profile_id) {
        throw new Error('Invoice does not have a bank profile')
      }
      
      // For non-cash deals, recipient_email contains the bank contact email
      // We can use it directly, but also fetch bank profile to get all contacts for sending to multiple recipients
      const bankProfileRef = doc(db, 'bank_profiles', invoiceData.bank_profile_id)
      const bankProfileDoc = await getDoc(bankProfileRef)
      
      let emailAddresses: string[] = []
      let bankName = 'Bank' // Default bank name
      
      if (bankProfileDoc.exists()) {
        const bankProfile = { id: bankProfileDoc.id, ...bankProfileDoc.data() } as BankProfile
        bankName = bankProfile.bankName || 'Bank'

        emailAddresses = resolveBankProfileEmails(bankProfile, invoiceData.recipient_email)
      }
      
      // Fallback to recipient_email if no bank contacts found
      if (emailAddresses.length === 0) {
        if (invoiceData.recipient_email) {
          emailAddresses = [invoiceData.recipient_email]
        } else {
          throw new Error('No valid email addresses found for bank invoice')
        }
      }
      
      // Convert PDF to base64
      const base64Data = await urlToBase64(invoiceData.pdf_url)
      
      // Extract file extension for proper filename
      const fileName = invoiceData.pdf_url.split('/').pop()?.split('?')[0] || `invoice_${invoiceData.invoice_number}.pdf`
      
      // Create email HTML
      const html = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Invoice ${invoiceData.invoice_number}</title>
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
          </head>
          <body style="font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif; background-color: #f5f5f5; margin: 0; padding: 0;">
            <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); margin-top: 40px; margin-bottom: 40px; padding: 32px;">
              <h1 style="font-size: 24px; font-weight: 500; color: #1976d2; margin-bottom: 24px;">
                Invoice ${invoiceData.invoice_number}
              </h1>
              <p style="font-size: 16px; color: #333333; line-height: 1.6; margin-bottom: 16px;">
                Please find the attached invoice for your review.
              </p>
              <p style="font-size: 16px; color: #333333; line-height: 1.6; margin-bottom: 16px;">
                Invoice Number: <strong>${invoiceData.invoice_number}</strong>
              </p>
              ${invoiceData.client_name ? `<p style="font-size: 16px; color: #333333; line-height: 1.6; margin-bottom: 16px;">Client: <strong>${invoiceData.client_name}</strong></p>` : ''}
              <p style="font-size: 14px; color: #666666; line-height: 1.6; border-top: 1px solid #e0e0e0; padding-top: 24px; margin-top: 24px;">
                This is an automated notification from the Optic Truck Works Portal.
              </p>
            </div>
          </body>
        </html>
      `
      
      // Prepare the email data
      const emailData = {
        to: emailAddresses.join(','), // Send to all bank contacts
        message: {
          subject: `Invoice ${invoiceData.invoice_number} - ${bankName}`,
          html,
          text: `Please find the attached invoice ${invoiceData.invoice_number} for your review.`,
          attachments: [
            {
              filename: fileName,
              content: base64Data,
              encoding: 'base64'
            }
          ]
        }
      }
      
      // Add the email to the mail collection
      await addDoc(collection(db, 'mail'), emailData)
      
      // Update invoice status to sent and update history
      const updateHistory = invoiceData.updateHistory || []
      updateHistory.push({
        status: 'sent',
        timestamp: new Date().toISOString(),
      })
      
      await updateDoc(invoiceRef, {
        status: 'sent',
        status_updated_at: new Date().toISOString(),
        updateHistory,
      })
      
      return { invoiceId, emailAddresses }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey })
      showSuccess(`PDF invoice sent to ${data.emailAddresses.length} recipient(s)`)
    },
    onError: (error) => {
      showError(`Failed to send PDF invoice: ${error instanceof Error ? error.message : 'Unknown error'}`)
    },
  })

  const sendPdfInvoiceToClient = useMutation({
    mutationFn: async (invoiceId: string) => {
      // Get the invoice
      const invoiceRef = doc(db, 'invoices', invoiceId)
      const invoiceDoc = await getDoc(invoiceRef)
      
      if (!invoiceDoc.exists()) {
        throw new Error('Invoice not found')
      }
      
      const invoiceData = invoiceDoc.data() as Invoice
      
      if (!invoiceData.pdf_url) {
        throw new Error('Invoice does not have a PDF URL')
      }
      
      // For cash deals, recipient_email should be client email
      // For non-cash deals, recipient_email should be bank contact email
      const recipientEmail = invoiceData.recipient_email || invoiceData.client_email
      
      if (!recipientEmail) {
        throw new Error('Invoice does not have a recipient email address')
      }
      
      // Convert PDF to base64
      const base64Data = await urlToBase64(invoiceData.pdf_url)
      
      // Extract file extension for proper filename
      const fileName = invoiceData.pdf_url.split('/').pop()?.split('?')[0] || `invoice_${invoiceData.invoice_number}.pdf`
      
      // Get recipient name
      const recipientName = invoiceData.recipient_first_name && invoiceData.recipient_last_name
        ? `${invoiceData.recipient_first_name} ${invoiceData.recipient_last_name}`
        : invoiceData.client_name || 'Client'
      
      // Create email HTML
      const html = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Invoice ${invoiceData.invoice_number}</title>
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
          </head>
          <body style="font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif; background-color: #f5f5f5; margin: 0; padding: 0;">
            <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); margin-top: 40px; margin-bottom: 40px; padding: 32px;">
              <h1 style="font-size: 24px; font-weight: 500; color: #1976d2; margin-bottom: 24px;">
                Invoice ${invoiceData.invoice_number}
              </h1>
              <p style="font-size: 16px; color: #333333; line-height: 1.6; margin-bottom: 16px;">
                Dear ${recipientName},
              </p>
              <p style="font-size: 16px; color: #333333; line-height: 1.6; margin-bottom: 16px;">
                Please find the attached invoice for your review.
              </p>
              <p style="font-size: 16px; color: #333333; line-height: 1.6; margin-bottom: 16px;">
                Invoice Number: <strong>${invoiceData.invoice_number}</strong>
              </p>
              <p style="font-size: 14px; color: #666666; line-height: 1.6; border-top: 1px solid #e0e0e0; padding-top: 24px; margin-top: 24px;">
                This is an automated notification from the Optic Truck Works Portal.
              </p>
            </div>
          </body>
        </html>
      `
      
      // Prepare the email data
      const emailData = {
        to: recipientEmail,
        message: {
          subject: `Invoice ${invoiceData.invoice_number} - Optic Truck Works`,
          html,
          text: `Dear ${recipientName},\n\nPlease find the attached invoice ${invoiceData.invoice_number} for your review.`,
          attachments: [
            {
              filename: fileName,
              content: base64Data,
              encoding: 'base64'
            }
          ]
        }
      }
      
      // Add the email to the mail collection
      await addDoc(collection(db, 'mail'), emailData)
      
      // Update invoice status to sent and update history
      const updateHistory = invoiceData.updateHistory || []
      updateHistory.push({
        status: 'sent',
        timestamp: new Date().toISOString(),
      })
      
      await updateDoc(invoiceRef, {
        status: 'sent',
        status_updated_at: new Date().toISOString(),
        updateHistory,
      })
      
      return { invoiceId, recipientEmail }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey })
      showSuccess(`PDF invoice sent to ${data.recipientEmail}`)
    },
    onError: (error) => {
      showError(`Failed to send PDF invoice: ${error instanceof Error ? error.message : 'Unknown error'}`)
    },
  })

  const deleteInvoices = useMutation({
    mutationFn: async (invoiceIds: string[]) => {
      if (invoiceIds.length === 0) {
        throw new Error('No invoices to delete')
      }

      // Use batch write for efficient deletion
      const batch = writeBatch(db)
      
      invoiceIds.forEach(invoiceId => {
        const invoiceRef = doc(db, 'invoices', invoiceId)
        batch.delete(invoiceRef)
      })
      
      await batch.commit()
      
      return { deletedCount: invoiceIds.length }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey })
      showSuccess(`Successfully deleted ${data.deletedCount} invoice${data.deletedCount !== 1 ? 's' : ''}`)
    },
    onError: (error) => {
      showError(`Failed to delete invoices: ${error instanceof Error ? error.message : 'Unknown error'}`)
    },
  })

  const markPdfInvoiceFunded = useMutation({
    mutationFn: async (invoiceId: string) => {
      const invoiceRef = doc(db, 'invoices', invoiceId)
      const invoiceDoc = await getDoc(invoiceRef)
      if (!invoiceDoc.exists()) {
        throw new Error('Invoice not found')
      }
      const invoiceData = invoiceDoc.data() as Invoice
      if (!invoiceData.pdf_url) {
        throw new Error('Invoice is not a PDF invoice')
      }
      const vehicleIds = invoiceData.vehicle_ids ?? []
      if (vehicleIds.length === 0) {
        throw new Error('Invoice has no vehicles')
      }
      const inventoryType = invoiceData.inventory_type ?? 'master_inventory'
      const currentDate = new Date().toISOString()

      await Promise.all(
        vehicleIds.map((vehicleId) =>
          updateDoc(doc(db, inventoryType, vehicleId), {
            funded: true,
            funded_date: currentDate,
          })
        )
      )

      return { invoiceId, vehicleCount: vehicleIds.length }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoice-vehicles'] })
      showSuccess(`Marked ${data.vehicleCount} vehicle(s) as funded`)
    },
    onError: (error) => {
      showError(`Failed to mark as funded: ${error instanceof Error ? error.message : 'Unknown error'}`)
    },
  })

  const unmarkPdfInvoiceFunded = useMutation({
    mutationFn: async (invoiceId: string) => {
      const invoiceRef = doc(db, 'invoices', invoiceId)
      const invoiceDoc = await getDoc(invoiceRef)
      if (!invoiceDoc.exists()) {
        throw new Error('Invoice not found')
      }
      const invoiceData = invoiceDoc.data() as Invoice
      if (!invoiceData.pdf_url) {
        throw new Error('Invoice is not a PDF invoice')
      }
      const vehicleIds = invoiceData.vehicle_ids ?? []
      if (vehicleIds.length === 0) {
        throw new Error('Invoice has no vehicles')
      }
      const inventoryType = invoiceData.inventory_type ?? 'master_inventory'

      await Promise.all(
        vehicleIds.map((vehicleId) =>
          updateDoc(doc(db, inventoryType, vehicleId), {
            funded: false,
            funded_date: null,
          })
        )
      )

      return { invoiceId, vehicleCount: vehicleIds.length }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoice-vehicles'] })
      showSuccess(`Cleared funded status for ${data.vehicleCount} vehicle(s)`)
    },
    onError: (error) => {
      showError(`Failed to undo funded: ${error instanceof Error ? error.message : 'Unknown error'}`)
    },
  })

  return {
    invoices: invoices || [],
    isLoading: isLoading || isLoadingVehicles || isLoadingBankProfiles,
    isError,
    error,
    sendInvoice: sendInvoice.mutate,
    sendInvoiceAsync: sendInvoice.mutateAsync,
    isSendingInvoice: sendInvoice.isPending,
    sendPdfInvoiceToBank: sendPdfInvoiceToBank.mutate,
    sendPdfInvoiceToBankAsync: sendPdfInvoiceToBank.mutateAsync,
    isSendingPdfInvoice: sendPdfInvoiceToBank.isPending,
    sendPdfInvoiceToClient: sendPdfInvoiceToClient.mutate,
    sendPdfInvoiceToClientAsync: sendPdfInvoiceToClient.mutateAsync,
    isSendingPdfInvoiceToClient: sendPdfInvoiceToClient.isPending,
    deleteInvoices: deleteInvoices.mutate,
    deleteInvoicesAsync: deleteInvoices.mutateAsync,
    isDeletingInvoices: deleteInvoices.isPending,
    markPdfInvoiceFunded: markPdfInvoiceFunded.mutate,
    markPdfInvoiceFundedAsync: markPdfInvoiceFunded.mutateAsync,
    isMarkingPdfInvoiceFunded: markPdfInvoiceFunded.isPending,
    unmarkPdfInvoiceFunded: unmarkPdfInvoiceFunded.mutate,
    unmarkPdfInvoiceFundedAsync: unmarkPdfInvoiceFunded.mutateAsync,
    isUnmarkingPdfInvoiceFunded: unmarkPdfInvoiceFunded.isPending,
    generateInvoiceNumber,
    refetch,
    vehiclesMap,
    bankProfilesMap,
  }
}

